import { camelize } from '@ember/string';
import { setViaAPI } from 'cio/utils/transactional/code-snippets';
import { titleize } from 'cio/helpers/titleize';
import { TransactionalMessageChannelType } from 'cio/models/transactional-message';

type TransactionalCodeSnippetValue =
  | string
  | number
  | string[]
  | number[]
  | Record<string, any>[]
  | TransactionalCodeSnippetMessage;

export type TransactionalCodeSnippetLanguage = 'Node.js' | 'Python' | 'Go' | 'Ruby' | 'cURL';

export type TransactionalCodeSnippetMessage = {
  [key: string]: TransactionalCodeSnippetValue;
};

// The types of values we render: currently only a class and an object
type ValueType = 'class' | 'object';

type RendererOptions = {
  valueType: ValueType;
};

type TokensForLanguage = {
  classStart: Record<TransactionalMessageChannelType, string>;
  classEnd: string;
  objectEnd: string;
  objectKeyTerminator: string;
  objectLastKeyTerminator: string;
  statementTerminator: string;
  arrayStart: string;
  arrayEnd: string;
  keyValueString: (key: string, value: string, opts: RendererOptions) => string;
  keyValueNumber: (key: string, value: number, opts: RendererOptions) => string;
  keyObject: (key: string, opts: RendererOptions) => string;
  keyArray: (key: string, opts: RendererOptions) => string;
};

const tokensForLanguages: Record<TransactionalCodeSnippetLanguage, TokensForLanguage & Record<string, any>> = {
  'Node.js': {
    classStart: {
      [TransactionalMessageChannelType.Email]: 'new SendEmailRequest({',
      [TransactionalMessageChannelType.Push]: 'new SendPushRequest({',
    },
    classEnd: '})',
    objectEnd: '}',
    objectKeyTerminator: ',',
    objectLastKeyTerminator: ',',
    statementTerminator: ';',
    arrayStart: '[',
    arrayEnd: ']',

    keyValueString(key: string, value: string) {
      return `${this.formatKey(key)}: "${value}"`;
    },

    keyValueNumber(key: string, value: number) {
      return `${this.formatKey(key)}: ${value}`;
    },

    keyObject(key: string) {
      return `${this.formatKey(key)}: {`;
    },

    keyArray(key: string) {
      return `${this.formatKey(key)}: [`;
    },

    formatKey(key: string) {
      return key.includes(' ') ? `"${key}"` : key;
    },
  },

  Python: {
    classStart: {
      [TransactionalMessageChannelType.Email]: 'SendEmailRequest(',
      [TransactionalMessageChannelType.Push]: 'SendPushRequest(',
    },
    classEnd: ')',
    objectEnd: '}',
    objectKeyTerminator: ',',
    objectLastKeyTerminator: '',
    statementTerminator: '',
    arrayStart: '[',
    arrayEnd: ']',

    keyValueString(key: string, value: string, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}${this.keyValueSeparator(opts)}"${value}"`;
    },

    keyValueNumber(key: string, value: number, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}${this.keyValueSeparator(opts)}${value}`;
    },

    keyObject(key: string, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}${this.keyValueSeparator(opts)}{`;
    },

    keyArray(key: string, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}${this.keyValueSeparator(opts)}[`;
    },

    formatKey(key: string, opts: RendererOptions) {
      // "from" is a protected term in Python
      if (key === 'from') {
        key = `_${key}`;
      }
      return opts.valueType === 'object' ? `"${key}"` : key;
    },

    keyValueSeparator(opts: RendererOptions) {
      return opts.valueType === 'object' ? ': ' : '=';
    },
  },

  Go: {
    classStart: {
      [TransactionalMessageChannelType.Email]: 'customerio.SendEmailRequest{',
      [TransactionalMessageChannelType.Push]: 'customerio.SendPushRequest{',
    },
    classEnd: '}',
    objectEnd: '}',
    objectKeyTerminator: ',',
    objectLastKeyTerminator: ',',
    statementTerminator: '',
    arrayStart: '[]interface{}{',
    arrayEnd: '}',

    keyValueString(key: string, value: string, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}: "${value}"`;
    },

    keyValueNumber(key: string, value: number, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}: ${value}`;
    },

    keyObject(key: string, opts: RendererOptions) {
      // Identifiers is defined in the `go-customerio` library as a map[string]string,
      // so we make a special case exception for it. Otherwise, everything is considered
      // unknown and mapped to an interface{}.
      if (key === 'identifiers') {
        return `${this.formatKey(key, opts)}: map[string]string{`;
      } else {
        return `${this.formatKey(key, opts)}: map[string]interface{}{`;
      }
    },

    keyArray(key: string, opts: RendererOptions) {
      return `${this.formatKey(key, opts)}: []interface{}{`;
    },

    formatKey(key: string, opts: RendererOptions) {
      if (opts.valueType === 'object') {
        return `"${key}"`;
      } else {
        // Transform a JS-like key to a Go struct key. For example:
        // `message_data` -> `MessageData`
        // `content_id` -> `ContentID`
        return titleize([camelize(key)]).replace(/Id$/, 'ID');
      }
    },
  },

  Ruby: {
    classStart: {
      [TransactionalMessageChannelType.Email]: 'Customerio::SendEmailRequest.new(',
      [TransactionalMessageChannelType.Push]: 'Customerio::SendPushRequest.new(',
    },
    classEnd: ')',
    objectEnd: '}',
    objectKeyTerminator: ',',
    objectLastKeyTerminator: ',',
    statementTerminator: '',
    arrayStart: '[',
    arrayEnd: ']',

    keyValueString(key: string, value: string) {
      return `${key}: "${value}"`;
    },

    keyValueNumber(key: string, value: number) {
      return `${key}: ${value}`;
    },

    keyObject(key: string) {
      return `${key}: {`;
    },

    keyArray(key: string) {
      return `${key}: [`;
    },
  },

  cURL: {
    classStart: {
      [TransactionalMessageChannelType.Email]: '{',
      [TransactionalMessageChannelType.Push]: '{',
    },
    classEnd: '}',
    objectEnd: '}',
    objectKeyTerminator: ',',
    objectLastKeyTerminator: '',
    statementTerminator: '',
    arrayStart: '[',
    arrayEnd: ']',

    keyValueString(key: string, value: string) {
      return `"${key}": "${value}"`;
    },

    keyValueNumber(key: string, value: number) {
      return `"${key}": ${value}`;
    },

    keyObject(key: string) {
      return `"${key}": {`;
    },

    keyArray(key: string) {
      return `"${key}": [`;
    },
  },
} as const;

export default class MessageRenderer {
  private output: string[] = [];
  private whitespace = 0;
  private tokens?: (typeof tokensForLanguages)[TransactionalCodeSnippetLanguage];

  render(
    message: TransactionalCodeSnippetMessage,
    language: TransactionalCodeSnippetLanguage,
    channelType: TransactionalMessageChannelType,
  ) {
    this.output = [];

    this.tokens = tokensForLanguages[language];

    // Start the object and indent before we start writing
    this.writeNewline(this.tokens.classStart[channelType]);
    this.indent();

    this.renderVariables(message, { valueType: 'class' });

    // Outdent and add terminate the object that we're done writing
    this.outdent();
    this.writeNewline(`${this.tokens.classEnd}${this.tokens.statementTerminator}`);

    return this.output.join('\n');
  }

  private writeNewline(str: string) {
    this.output.push(`${Array(this.whitespace).fill(' ').join('')}${str}`);
  }

  private write(str: string) {
    if (this.output.length === 0) {
      return;
    }

    let currLine = this.output[this.output.length - 1];
    this.output[this.output.length - 1] = `${currLine}${str}`;
  }

  private indent(n = 1) {
    this.whitespace += 2 * n;
  }

  private outdent(n = 1) {
    this.whitespace -= 2 * n;
  }

  private renderVariables(message: TransactionalCodeSnippetMessage, opts: RendererOptions) {
    let keys = Object.keys(message);

    keys.forEach((key, idx) => {
      this.renderKeyValue(key, message[key], idx === keys.length - 1, opts);
    });
  }

  private renderKeyValue(key: string, value: TransactionalCodeSnippetValue, isLast: boolean, opts: RendererOptions) {
    let { tokens } = this;
    if (!tokens) {
      throw new Error('`tokens` must be set before calling renderKeyValue');
    }

    if (typeof value === 'string') {
      this.writeNewline(tokens.keyValueString(key, value, opts));
    } else if (typeof value === 'number') {
      this.writeNewline(tokens.keyValueNumber(key, value, opts));
    } else if (Array.isArray(value)) {
      this.renderArray(key, opts);
    } else if (value) {
      if (Object.keys(value).length <= 0) {
        return;
      }

      this.renderObject(key, value, opts);
    }

    // Some languages require the last key-value pair to end with a specific
    // terminator (for example: Go wants a comma) but some others consider that
    // invalid (JSON), so we have to differentiate based on the index of the
    // current key.
    this.write(isLast ? tokens.objectLastKeyTerminator : tokens.objectKeyTerminator);
  }

  private renderObject(key: string, value: TransactionalCodeSnippetMessage, opts: RendererOptions) {
    let { tokens } = this;
    if (!tokens) {
      throw new Error('`tokens` must be set before calling renderObject');
    }

    this.writeNewline(tokens.keyObject(key, opts));
    this.indent();

    // For some languages, there are syntactical differences between how objects and classes
    // are rendered. We start by rendering a class, and then as we move down the tree, we may
    // encounter and render an object (a map in Go, a dict in Python).
    //
    // When we do so, we change the `valueType` here to "object" so that the tokens are correct.
    this.renderVariables(value, { valueType: 'object' });
    this.outdent();
    this.writeNewline(tokens.objectEnd);
  }

  private renderArray(key: string, opts: RendererOptions) {
    let { tokens } = this;
    if (!tokens) {
      throw new Error('`tokens` must be set before calling renderArray');
    }

    // Arrays are a special case where we don't try to render anything
    // recursively. Instead, we just indicate that it's an array and on the
    // same line, indicate that it's supposed to be set via API.
    this.writeNewline(tokens.keyArray(key, opts));
    this.write(setViaAPI);
    this.write(tokens.arrayEnd);
  }
}
